import { Modal, TextField } from "@mui/material";
import { FC, useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  usePageContext,
  useViewActions,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  entityIdSF,
  Log,
  resolveScope,
  useViewScopePermission,
  viewChangesApplyingAtom,
  ViewDto,
} from "@ryde/shared";

import {
  DropdownInputByControl,
  ModalContainerStyled,
  ModalContentStyled,
  ModalFooterStyled,
  ModalHeader,
  MuiButton,
  MuiSwitch,
} from "../../../controls";
import { ViewContext } from "../../view.context";
import { viewEditValidation } from "./view-edit-validation";
import { IViewEditFormValues } from "./view-edit.model";
import { ViewEditDialogContainer } from "./view-edit.styled";

interface IDialogViewEditProps {
  onClose: () => void;
  view?: ViewDto;
}

export const DialogViewEdit: FC<IDialogViewEditProps> = ({ view, onClose }) => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const entityId = useRecoilValue(entityIdSF(entity));
  const { scopeOptions, isSaveButtonDisabled } = useViewScopePermission();
  const { createViewHandler, updateViewHandler, generateDefaultViewName } =
    useViewActions();
  const setIsUpdating = useSetRecoilState(viewChangesApplyingAtom);

  const resolver = useYupValidationResolver(viewEditValidation);
  const isEdit = Boolean(view);
  const { setIsDropdownOpen } = useContext(ViewContext);
  const methods = useForm<IViewEditFormValues>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      name: isEdit ? view?.name : generateDefaultViewName(),
      isDefault: isEdit ? view?.isDefault : false,
      scope: isEdit ? resolveScope(view as ViewDto) : scopeOptions.at(0),
    },
    resolver,
  });
  const isSaveButtonDisabledResolved =
    isSaveButtonDisabled || !methods.formState.isValid;
  const isDefaultWatch = methods.watch("isDefault", false);
  const title = isEdit ? t("view.update") : t("view.create");

  const onAccept = async (): Promise<void> => {
    if (!methods.formState.isValid) {
      return;
    }

    const values = methods.getValues();

    if (!entityId) {
      Log.warnAndToast(t("errors.noEntityFound"));
      return;
    }

    if (isEdit) {
      setIsUpdating(true);
      await updateViewHandler({
        viewId: view?.id as number,
        name: values.name,
        isDefault: values.isDefault,
        scope: values.scope,
        createViewEntityId: entityId,
      });
      setIsUpdating(false);
      setIsDropdownOpen?.(false);
    } else {
      await createViewHandler({
        name: values.name,
        isDefault: values.isDefault,
        scope: values.scope,
        createViewEntityId: entityId,
      });
    }

    onClose();
  };

  const onCancel = (): void => {
    onClose();
  };

  return (
    <Modal open onClose={onClose}>
      <ModalContainerStyled>
        <ModalHeader onClose={onClose}>{title}</ModalHeader>
        <ModalContentStyled>
          <ViewEditDialogContainer>
            <FormProvider {...methods}>
              <TextField
                label={`${t(`view.formLabel.name`)}`}
                fullWidth
                data-e2e="view-name-modal-input"
                required
                {...methods.register("name")}
              />
              <DropdownInputByControl
                name="scope"
                formLabel={t("view.scopeLabel")}
                control={methods.control}
                formValidation={{
                  type: "string",
                  rules: [{ validation: "required" }],
                }}
                formAttributes={{
                  selectItems: scopeOptions.map(scopeItem => ({
                    value: scopeItem,
                    label: t(`view.${scopeItem}`),
                  })),
                }}
              />
              <MuiSwitch
                onChange={e => methods.setValue("isDefault", e.target.checked)}
                checked={isDefaultWatch}
                label={t(`view.formLabel.default`)}
                e2eData="save-view-switch"
              />
            </FormProvider>
          </ViewEditDialogContainer>
        </ModalContentStyled>
        <ModalFooterStyled>
          <MuiButton
            muiStyle="primary"
            muiVariant="outlined"
            onClick={onCancel}
            e2eData="cancel-modal-button"
          >
            {t(`general.CANCEL`)}
          </MuiButton>
          <MuiButton
            disabled={isSaveButtonDisabledResolved}
            onClick={onAccept}
            e2eData="ok-modal-button"
          >
            {t(`general.SAVE`)}
          </MuiButton>
        </ModalFooterStyled>
      </ModalContainerStyled>
    </Modal>
  );
};
